//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { ReactComponent as Exclamation } from 'assets/exclamation-fill.svg';
import { TriggerText, classifyTrigger } from 'components/TriggerAttributionMessage';
import { useAuth } from 'contexts/AuthProvider';
import { useModal } from 'contexts/ModalProvider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UseSseFlashStatusProps {
  /* 以下兩個僅供「保留待用」的 Archiving Error 流程使用,詳見檔案下方區塊說明 */
  onPauseUpgradeProgress?: () => void;
  onResumeUpgradeProgress?: () => void;
}

/* 對應後端 sse.service.ts 的 EventTriggerSource,標記非 Web 觸發的來源 */
export enum EventTriggerSource {
  ResetButton = 1,
  Lcm = 2,
  Web = 3,
  Schedule = 4,
}

interface ArchiveSystemContentProps {
  isRestart?: boolean;
  triggerUser?: string;
  triggerClientId?: string;
  triggerSource?: EventTriggerSource;
}

// ───────────────────────────────────────────────────────────────
// 【保留待用】Archiving Error 失敗提示
//
// d56570c2「移除 Archiving Error 錯誤對話框」後,EVENT_TRIGGER 的
// handleFlashStatusEvent 只看 payload.flow、忽略 errorMessage,
// 因此以下能力目前沒有呼叫端。保留以便日後恢復該 UI。
// ───────────────────────────────────────────────────────────────

const SYNC_FLASH_FAIL_TIME = 5 * 1000;

const FlashStatusFailContent = ({ onArchiveFailed }: { onArchiveFailed: () => void }) => {
  const { t } = useTranslation();
  const [val, setVal] = useState<number>(0);
  const { closeCustomModal } = useModal();
  const [count, setCount] = useState<number>(SYNC_FLASH_FAIL_TIME / 1000);

  useEffect(() => {
    const step = 100 / (SYNC_FLASH_FAIL_TIME / 1000);
    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev > 0) {
          setVal((v) => v + step);
          return prev - 1;
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (count === 0) {
      const timer = setTimeout(() => {
        closeCustomModal();
        onArchiveFailed();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, closeCustomModal, onArchiveFailed]);

  const style = { '--value': val, '--size': '6rem', '--thickness': '6px' } as React.CSSProperties;

  return (
    <div data-testid="flash-status-fail-dialog">
      <div className="flex justify-start">
        <Exclamation className="w-10 h-10 inline mr-2 rounded-full fill-yellow-400 bg-yellow-50 p-2" />
        <h3 className="font-bold text-lg inline">{t('modal.archivalErrorAndSystemRestart')}</h3>
      </div>
      <div className="flex flex-col items-center">
        <div className="py-4 pl-12 pr-2">{t('modal.archivalErrorAndSystemRestartMsg')}</div>
        <div className="radial-progress" style={style}>
          <div className="flex justify-center items-center w-[92px] h-[92px] rounded-full border-gray-300 border-2">{count}</div>
        </div>
      </div>
    </div>
  );
};

const ArchiveSystemContent = ({ isRestart, triggerUser, triggerClientId, triggerSource }: ArchiveSystemContentProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const trigger = classifyTrigger(triggerUser, triggerClientId, user.email);

  /* LCM 與實體按鈕目前顯示相同文案,不予區分 */
  const isDeviceTrigger = isRestart && (triggerSource === EventTriggerSource.ResetButton || triggerSource === EventTriggerSource.Lcm);
  const isScheduledRestart = isRestart && triggerSource === EventTriggerSource.Schedule;
  const isSameUserOtherDevice = isRestart && trigger.isSameUserOtherDevice;
  const isOtherUser = isRestart && trigger.isOtherUser;

  return (
    <>
      {isDeviceTrigger && <TriggerText>{t('modal.systemRestartViaDeviceBtn')}</TriggerText>}
      {isSameUserOtherDevice && <TriggerText>{t('modal.restartTriggeredAnotherDevice')}</TriggerText>}
      {isOtherUser && <TriggerText>{t('modal.archivingSystemRecordsTriggerUserMsg', { triggerUser })}</TriggerText>}
      {isScheduledRestart && <TriggerText>{t('modal.archivingSystemRecordsScheduledRestart')}</TriggerText>}
      <span>{t('modal.archivingSystemRecordsMsg')}</span>
    </>
  );
};

export const useSseFlashStatus = ({ onPauseUpgradeProgress, onResumeUpgradeProgress }: UseSseFlashStatusProps = {}) => {
  const { t } = useTranslation();
  const { addRadialProgressModal, addCustomModal, closeCustomModal } = useModal();
  const SYNC_FLASH_TIME = 3 * 60 * 1000;

  /* 【保留待用】僅供下方 Archiving Error 區塊使用 */
  const callbacksRef = useRef<{
    onArchiveComplete?: () => void;
    onArchiveFailed?: () => void;
  }>({});
  const currentFlowRef = useRef<string | undefined>();

  const displayArchivSystemRadialProgressModal = useCallback(
    (props?: ArchiveSystemContentProps) => {
      addRadialProgressModal({
        open: true,
        name: 'archiveSystemRecord',
        title: t('modal.archivingSystemRecords'),
        message: <ArchiveSystemContent {...props} />,
        type: 'warning',
        totalTime: SYNC_FLASH_TIME,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addRadialProgressModal, t, SYNC_FLASH_TIME]
  );

  /*
   * props 由呼叫端(handleFlashStatusEvent)從事件 payload 傳入,
   * 讓收尾畫面能沿用該次操作真正的觸發者資訊,而不必依賴任何跨 hook 實例共享的狀態。
   */
  const endRadialProgressModal = useCallback(
    (callback: () => void, props?: ArchiveSystemContentProps) => {
      addRadialProgressModal({
        open: true,
        name: 'endArchivingRadial',
        title: t('modal.archivingSystemRecords'),
        message: <ArchiveSystemContent {...props} />,
        type: 'warning',
        totalTime: 1000,
        finishFunction: () => {
          setTimeout(() => callback(), 1000);
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addRadialProgressModal, t]
  );

  // ── 【保留待用】Archiving Error 失敗提示(目前無呼叫端)──────
  const displayFlashStatusFailDialog = useCallback(
    (flow?: string) => {
      const flashStatusFailModal = () => {
        if (flow === 'upgrade' && onPauseUpgradeProgress) onPauseUpgradeProgress();

        addCustomModal({
          open: true,
          name: 'flashStatusFail',
          content: (
            <FlashStatusFailContent
              onArchiveFailed={() => {
                if (flow === 'upgrade' && onResumeUpgradeProgress) onResumeUpgradeProgress();
                callbacksRef.current.onArchiveFailed?.();
              }}
            />
          ),
          width: 500,
        });
      };

      if (flow) {
        closeCustomModal();
        setTimeout(() => {
          flashStatusFailModal();
        }, 100);
      } else {
        endRadialProgressModal(() => {
          flashStatusFailModal();
        });
      }
    },
    [addCustomModal, closeCustomModal, endRadialProgressModal, onPauseUpgradeProgress, onResumeUpgradeProgress]
  );

  const startSseFlashStatus = useCallback((callbacks?: { onArchiveComplete?: () => void; onArchiveFailed?: () => void }, flow?: string) => {
    if (callbacks) callbacksRef.current = callbacks;
    if (flow) currentFlowRef.current = flow;
  }, []);

  return {
    displayArchivSystemRadialProgressModal,
    endRadialProgressModal,
    startSseFlashStatus, // 保留待用:Archiving Error 流程,目前無呼叫端
    displayFlashStatusFailDialog,
    callbacksRef,
  };
};
